import { LienWaiverHistoryEntry } from './lien-waivers-draws.util';

/**
 * Rows added in the Recap of Previous Reports editor start empty. They stay in the editor while the
 * user fills them in, but a row still empty at save/export time is dropped, so it never reaches the
 * server or prints as a line of "--" in the report.
 */

const isEmpty = (value: unknown): boolean => value === null || value === undefined || String(value).trim() === '';

/** An application row with nothing entered (its heading and draw are set automatically). */
export const isBlankRecapApplication = (row: any): boolean =>
  [
    row?.applicationNumber,
    row?.applicationDate,
    row?.requestedAmount,
    row?.approvedAmount,
    row?.retainage,
    row?.percentRequested,
    row?.percentComplete,
    row?.contractAmount,
  ].every(isEmpty);

/** A lien waiver row with nothing entered (contractor and status are prefilled on add). */
export const isBlankLienWaiverHistoryEntry = (entry: Partial<LienWaiverHistoryEntry> | null | undefined): boolean =>
  [entry?.applicationNumber, entry?.applicationDate, entry?.amount].every(isEmpty);

export const withoutBlankRecapApplications = <T>(rows: T[] | undefined): T[] => (rows || []).filter(row => !isBlankRecapApplication(row));

/** Returns the lien waivers section with blank rows removed from its stored LienWaiverHistory JSON. */
export const withoutBlankLienWaiverHistory = <T extends { fields?: any[] }>(section: T | undefined): T | undefined => {
  const field = section?.fields?.find((f: any) => f.fieldName === 'LienWaiverHistory');
  if (!section || !field?.value) return section;
  let history: unknown;
  try {
    history = JSON.parse(field.value);
  } catch {
    return section;
  }
  if (!Array.isArray(history)) return section;
  const kept = history.filter(entry => !isBlankLienWaiverHistoryEntry(entry));
  if (kept.length === history.length) return section;
  return {
    ...section,
    fields: (section.fields || []).map((f: any) => (f === field ? { ...f, value: JSON.stringify(kept) } : f)),
  };
};
